import type { ReactNode } from 'react';
import { Badge, TBody, THead, Table, Td, Th, Tr } from '@/components/ui';
import { MAX_LISTED_RESULTS } from '@/logic/adjustments';
import {
  formatAzimuth,
  formatDMS,
  formatFixed,
  formatScale,
} from '@/logic/statistics';
import type {
  BlunderDetectionResults,
  BlunderResult,
  PointPair,
} from '@/types';
import { formatCount, pairLabelOf, pluralize } from './adjustmentDisplay';
import { RejectionAction } from './RejectionAction';

interface ViewProps {
  blunderResults: BlunderDetectionResults;
  pointPairs: PointPair[];
  /** Why recommendations cannot be previewed (outdated results). */
  previewBlockedReason?: string;
  onPreview: (rejectedIds: number[]) => void;
}

/** Pair with the largest residual and that residual's azimuth. */
const AtPair = ({
  result,
  pointPairs,
}: {
  result: BlunderResult;
  pointPairs: PointPair[];
}) => (
  <span className="inline-flex items-baseline gap-1.5">
    <span className="font-mono text-fg">
      {pairLabelOf(pointPairs, result.maxResidualPointId)}
    </span>
    <span className="text-xs text-fg-muted">
      {formatAzimuth(result.maxResidualAzimuth)}
    </span>
  </span>
);

const Rejected = ({
  result,
  initialLabel = 'No rejection',
}: {
  result: BlunderResult;
  initialLabel?: string;
}) =>
  result.isInitial || result.rejectedIds.length === 0 ? (
    <span className="text-fg-muted">{initialLabel}</span>
  ) : (
    <span className="inline-flex flex-wrap gap-x-2 gap-y-0.5 font-mono font-medium text-fg">
      {result.rejectedLabels.map((label, index) => (
        <span key={`${label}-${index}`}>
          {label}
          {index < result.rejectedLabels.length - 1 ? ',' : ''}
        </span>
      ))}
    </span>
  );

const Note = ({ children }: { children: ReactNode }) => (
  <p className="text-xs text-fg-muted">{children}</p>
);

const TableFrame = ({ children }: { children: ReactNode }) => (
  <div className="overflow-hidden rounded-lg border border-border">
    {children}
  </div>
);

/** Best rejection set at each level of a multi-level run. */
export const AllLevelsView = ({
  blunderResults,
  pointPairs,
  previewBlockedReason,
  onPreview,
}: ViewProps) => {
  const initial = blunderResults.initial;
  const summary = blunderResults.summary ?? [];
  return (
    <div className="space-y-3">
      <Note>
        Best rejection set at each level, ranked by the smallest maximum
        residual among{' '}
        {pluralize(
          blunderResults.candidateCount ?? blunderResults.activePointCount ?? 0,
          'candidate'
        )}
        {blunderResults.fixedScale !== undefined
          ? `, with scale held at ${blunderResults.fixedScale}`
          : ''}
        . Deeper levels always lower the maximum residual, so weigh each
        rejection against field evidence.
      </Note>
      <TableFrame>
        <Table
          aria-label="Best rejection set per level"
          className="[&_td]:whitespace-nowrap"
        >
          <THead>
            <Tr>
              <Th>Level</Th>
              <Th>Rejected</Th>
              <Th numeric>Max residual</Th>
              <Th>At pair</Th>
              <Th numeric>Rotation</Th>
              <Th numeric>Scale</Th>
              <Th numeric>Trials</Th>
              <Th className="w-0">
                <span className="sr-only">Action</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            {initial && (
              <Tr className="bg-surface-sunken/60">
                <Td className="text-fg-muted">0</Td>
                <Td>
                  <Rejected result={initial} />
                </Td>
                <Td numeric>{formatFixed(initial.maxResidual, 3)}</Td>
                <Td>
                  <AtPair result={initial} pointPairs={pointPairs} />
                </Td>
                <Td numeric>{formatDMS(initial.rotation)}</Td>
                <Td numeric>{formatScale(initial.scale, 6)}</Td>
                <Td numeric className="text-fg-muted">
                  1
                </Td>
                <Td />
              </Tr>
            )}
            {summary.map((result) => (
              <Tr key={result.level}>
                <Td>{result.level}</Td>
                <Td>
                  <Rejected result={result} />
                </Td>
                <Td numeric>{formatFixed(result.maxResidual, 3)}</Td>
                <Td>
                  <AtPair result={result} pointPairs={pointPairs} />
                </Td>
                <Td numeric>{formatDMS(result.rotation)}</Td>
                <Td numeric>{formatScale(result.scale, 6)}</Td>
                <Td numeric className="text-fg-muted">
                  {result.totalCombinations !== undefined
                    ? formatCount(result.totalCombinations)
                    : '—'}
                </Td>
                <Td className="pr-3">
                  <RejectionAction
                    compact
                    ids={result.rejectedIds}
                    labels={result.rejectedLabels}
                    pointPairs={pointPairs}
                    onPreview={onPreview}
                    disabledReason={previewBlockedReason}
                    variant="ghost"
                  />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </TableFrame>
    </div>
  );
};

/** Ranked rejection sets from a single-level run. */
export const SingleLevelView = ({
  blunderResults,
  pointPairs,
  previewBlockedReason,
  onPreview,
}: ViewProps) => {
  const results = blunderResults.results ?? [];
  const viewAll = blunderResults.viewAll === true;
  const total = blunderResults.totalCombinations ?? 0;
  const level = blunderResults.rejectionLevel ?? 1;
  const bestIndex = results.findIndex((result) => !result.isInitial);
  return (
    <div className="space-y-3">
      <Note>
        {formatCount(total)} {total === 1 ? 'combination' : 'combinations'} of{' '}
        {pluralize(level, 'pair')} from{' '}
        {pluralize(blunderResults.candidateCount ?? 0, 'candidate')}
        {blunderResults.fixedScale !== undefined
          ? `, scale held at ${blunderResults.fixedScale}`
          : ''}
        .{' '}
        {viewAll
          ? total > MAX_LISTED_RESULTS
            ? `Showing the best ${formatCount(MAX_LISTED_RESULTS)} of ${formatCount(total)} by RMS.`
            : 'Showing every combination, best RMS first.'
          : 'Showing the best by maximum residual.'}
      </Note>
      <TableFrame>
        <Table
          stickyHeader
          aria-label={`${level}-pair rejection sets`}
          className="[&_td]:whitespace-nowrap"
          containerClassName="max-h-[min(28rem,60vh)]"
        >
          <THead>
            <Tr>
              <Th numeric className="w-0">
                #
              </Th>
              <Th>Rejected</Th>
              <Th numeric>Max residual</Th>
              <Th>At pair</Th>
              {viewAll && <Th numeric>RMS</Th>}
              <Th numeric>Rotation</Th>
              <Th numeric>Scale</Th>
              <Th className="w-0">
                <span className="sr-only">Action</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            {results.map((result, index) => (
              <Tr
                key={`${index}-${result.rejectedIds.join('-')}`}
                className={
                  result.isInitial ? 'bg-surface-sunken/60' : undefined
                }
              >
                <Td numeric className="text-fg-muted">
                  {index + 1}
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-2">
                    <Rejected result={result} />
                    {index === bestIndex && <Badge tone="accent">Best</Badge>}
                  </span>
                </Td>
                <Td numeric>{formatFixed(result.maxResidual, 3)}</Td>
                <Td>
                  <AtPair result={result} pointPairs={pointPairs} />
                </Td>
                {viewAll && <Td numeric>{formatFixed(result.rmsTotal, 4)}</Td>}
                <Td numeric>{formatDMS(result.rotation)}</Td>
                <Td numeric>{formatScale(result.scale, 6)}</Td>
                <Td className="pr-3">
                  {!result.isInitial && (
                    <RejectionAction
                      compact
                      ids={result.rejectedIds}
                      labels={result.rejectedLabels}
                      pointPairs={pointPairs}
                      onPreview={onPreview}
                      disabledReason={previewBlockedReason}
                      variant={index === bestIndex ? 'secondary' : 'ghost'}
                    />
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </TableFrame>
    </div>
  );
};
